import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field } from "@/components/common";
import { Home, LogOut, Download, CreditCard, Building2, User, Wallet, FileText, ShieldCheck, CheckCircle2, Loader2, Landmark, QrCode, Copy, Bell, AlertTriangle, Receipt, Clock, History } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { tapi, setTenantToken, getTenantToken, inr, fmtDate, loadRazorpay } from "@/lib/tenantApi";
import { QRCodeCanvas } from "qrcode.react";

const STATUS_STYLES = {
  paid: "bg-emerald-100 text-emerald-700",
  partially_paid: "bg-amber-100 text-amber-700",
  overdue: "bg-rose-100 text-rose-700",
  issued: "bg-slate-100 text-slate-700",
  cancelled: "bg-slate-200 text-slate-500 line-through",
};

const KPI = ({ label, value, icon: Icon, tone = "slate" }) => (
  <Card className="p-5 border-slate-200 shadow-sm" data-testid={`kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <p className={`mt-2 font-heading font-bold tracking-tighter text-3xl text-${tone}-700`}>{value}</p>
      </div>
      <div className={`w-10 h-10 rounded-md bg-${tone}-50 flex items-center justify-center`}><Icon className={`w-5 h-5 text-${tone}-600`} /></div>
    </div>
  </Card>
);

export default function TenantPortalDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [invs, setInvs] = useState([]);
  const [outstanding, setOutstanding] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [manualOpen, setManualOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [notif, setNotif] = useState({ items: [], unread: 0 });
  const [notifOpen, setNotifOpen] = useState(false);
  const [payments, setPayments] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [tab, setTab] = useState("invoices");
  const [manual, setManual] = useState({ method: "UPI", reference: "", note: "", amount: 0 });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [m, ip, ns, ph] = await Promise.all([
        tapi.me(), tapi.invoices(),
        tapi.notifications().catch(() => ({ items: [], unread: 0 })),
        tapi.payments().catch(() => ({ payments: [], total_paid: 0 })),
      ]);
      setMe(m); setInvs(ip.invoices || []); setOutstanding(ip.outstanding || 0);
      setNotif(ns || { items: [], unread: 0 });
      setPayments(ph.payments || []); setTotalPaid(ph.total_paid || 0);
    } catch (e) {
      if (e.response?.status === 401) { setTenantToken(""); nav("/portal/login", { replace: true }); return; }
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (!getTenantToken()) { nav("/portal/login", { replace: true }); return; }
    refresh();
    const iv = setInterval(async () => {
      try { const ns = await tapi.notifications(); setNotif(ns); } catch {}
    }, 30000);
    return () => clearInterval(iv);
  }, []);
  const onOpenNotif = async (v) => {
    setNotifOpen(v);
    if (v && notif.unread > 0) {
      try { await tapi.markAllRead(); setNotif({ items: notif.items.map((n) => ({ ...n, status: "read" })), unread: 0 }); }
      catch {}
    }
  };

  const toggle = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };
  const selectableInvs = useMemo(() => invs.filter((i) => i.status !== "paid" && i.status !== "cancelled" && Number(i.balance || 0) > 0), [invs]);
  const selectedAmount = useMemo(() => selectableInvs.filter((i) => selected.has(i.id)).reduce((s, i) => s + Number(i.balance || 0), 0), [selectableInvs, selected]);

  const doDownload = async (inv) => {
    try {
      const t = getTenantToken();
      const r = await fetch(tapi.invoicePdfUrl(inv.id), { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) throw new Error("Download failed");
      const b = await r.blob();
      const u = URL.createObjectURL(b);
      const a = document.createElement("a"); a.href = u; a.download = `${inv.invoice_no}.pdf`; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("Could not download PDF"); }
  };

  const payOnline = async () => {
    if (!selected.size) return toast.error("Select at least one invoice to pay");
    setBusy(true);
    try {
      const order = await tapi.createOrder([...selected]);
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Payment library failed to load. Check your internet."); setBusy(false); return; }
      const options = {
        key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
        name: me?.landlord?.name || "RentLedger", description: "Rent payment",
        prefill: { name: order.tenant_name || "", email: order.tenant_email || "", contact: order.tenant_mobile || "" },
        theme: { color: "#059669" },
        handler: async (res) => {
          try {
            const v = await tapi.verifyPayment({
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success(`Payment received · ${v.receipt_no}`);
            setSelected(new Set()); refresh();
          } catch (e) { toast.error(e.response?.data?.detail || "Payment verification failed"); }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => toast.error("Payment failed. Please try again."));
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
    } finally { setBusy(false); }
  };

  const openManual = () => {
    if (!selected.size) return toast.error("Select the invoices you paid");
    setManual({ method: "UPI", reference: "", note: "", amount: selectedAmount });
    setManualOpen(true);
  };
  const submitManual = async () => {
    if (!manual.reference.trim()) return toast.error("Enter your UPI/UTR reference");
    if (!Number(manual.amount) || Number(manual.amount) <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await tapi.manualPay({ invoice_ids: [...selected], amount: Number(manual.amount), method: manual.method, reference: manual.reference.trim(), note: manual.note });
      toast.success("Payment marked. Awaiting landlord verification.");
      setManualOpen(false); setSelected(new Set()); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  const logout = () => { setTenantToken(""); nav("/portal/login", { replace: true }); };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  if (!me) return null;

  const lease = me.lease; const prop = me.property; const dep = me.deposit;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tenant-portal-dashboard">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
          <div className="flex items-center gap-2" data-testid="portal-owner-brand">
            {me.owner?.logo_image
              ? <img src={me.owner.logo_image} alt={me.owner.name} data-testid="portal-owner-logo" className="w-9 h-9 rounded-md object-contain bg-white border border-slate-200" />
              : <div className="w-9 h-9 rounded-md bg-emerald-500 flex items-center justify-center"><Home className="w-5 h-5 text-white" /></div>}
            <div>
              <p className="font-heading font-bold text-slate-900 leading-tight" data-testid="portal-owner-name">{me.owner?.name || me.landlord?.name}</p>
              <p className="text-xs text-slate-500 leading-tight">Tenant Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-semibold text-slate-800">{me.tenant?.name}</p>
              <p className="text-xs text-slate-500">{me.tenant?.code}</p>
            </div>
            <Popover open={notifOpen} onOpenChange={onOpenNotif}>
              <PopoverTrigger asChild>
                <button data-testid="portal-notif-bell" className="relative w-9 h-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center transition-colors" aria-label="Notifications">
                  <Bell className="w-4 h-4 text-slate-700" />
                  {notif.unread > 0 && (
                    <span data-testid="portal-notif-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">{notif.unread > 9 ? "9+" : notif.unread}</span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[22rem] p-0" data-testid="portal-notif-popover">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <p className="font-heading font-semibold text-slate-900">Notifications</p>
                    <p className="text-xs text-slate-500">{notif.items.length ? `${notif.items.length} recent` : "You'll see rent nudges and receipts here"}</p>
                  </div>
                  <Bell className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="max-h-[26rem] overflow-y-auto divide-y divide-slate-100">
                  {notif.items.length === 0 ? (
                    <div className="py-10 px-6 text-center text-slate-400 text-sm">No notifications yet.</div>
                  ) : notif.items.map((n) => {
                    const Icon = n.type === "rent-due" ? AlertTriangle : n.type === "payment-approved" ? CheckCircle2 : n.type === "payment-rejected" ? AlertTriangle : n.type === "payment-receipt" ? Receipt : Clock;
                    const tone = n.severity === "success" ? "text-emerald-600 bg-emerald-50" : n.severity === "warning" ? "text-amber-600 bg-amber-50" : "text-indigo-600 bg-indigo-50";
                    return (
                      <div key={n.id} data-testid={`portal-notif-item-${n.id}`} className={`p-3 flex gap-3 ${n.status !== "read" ? "bg-emerald-50/40" : ""}`}>
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${tone}`}><Icon className="w-4 h-4" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800">{n.title}</p>
                          <p className="text-xs text-slate-500 mt-0.5 leading-snug break-words">{n.message}</p>
                          <p className="text-[10px] text-slate-400 mt-1">{new Date(n.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
            <Button data-testid="portal-logout" variant="outline" size="sm" onClick={logout}><LogOut className="w-4 h-4 mr-1" /> Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPI label="Outstanding" value={inr(outstanding)} icon={Wallet} tone={outstanding > 0 ? "rose" : "emerald"} />
          <KPI label="Monthly Rent" value={inr(lease?.monthly_rent)} icon={Landmark} tone="indigo" />
          <KPI label="Security Deposit" value={inr(dep?.amount || lease?.security_deposit)} icon={ShieldCheck} tone="slate" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6 border-slate-200 shadow-sm">
            <Tabs value={tab} onValueChange={setTab}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <TabsList>
                  <TabsTrigger value="invoices" data-testid="tab-invoices"><FileText className="w-4 h-4 mr-1.5" /> Invoices</TabsTrigger>
                  <TabsTrigger value="history" data-testid="tab-history"><History className="w-4 h-4 mr-1.5" /> Payment History{payments.length > 0 && <span className="ml-1.5 text-xs bg-slate-200 rounded-full px-1.5 py-0.5">{payments.length}</span>}</TabsTrigger>
                </TabsList>
                {tab === "invoices" && selected.size > 0 && <Badge className="bg-emerald-100 text-emerald-700 self-start sm:self-auto" data-testid="selected-total">{selected.size} · {inr(selectedAmount)}</Badge>}
              </div>

              <TabsContent value="invoices" className="mt-0">
                <p className="text-xs text-slate-500 mb-3">Select outstanding invoices to pay together.</p>
                {invs.length === 0 ? (
                  <div className="border-2 border-dashed border-slate-200 rounded-md p-8 text-center text-slate-400 text-sm">No invoices yet — you're all clear.</div>
                ) : (
                  <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-sm">
                      <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                        <tr><th className="text-left py-2 px-2 w-8"></th><th className="text-left py-2 px-2">Invoice</th><th className="text-left py-2 px-2">Period</th><th className="text-left py-2 px-2">Due</th><th className="text-right py-2 px-2">Total</th><th className="text-right py-2 px-2">Balance</th><th className="text-center py-2 px-2">Status</th><th className="py-2 px-2 w-16"></th></tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {invs.map((i) => {
                          const canSelect = i.status !== "paid" && i.status !== "cancelled" && Number(i.balance || 0) > 0;
                          return (
                            <tr key={i.id} className="hover:bg-slate-50/50 transition-colors" data-testid={`inv-row-${i.invoice_no}`}>
                              <td className="py-3 px-2">{canSelect && <input type="checkbox" data-testid={`inv-check-${i.invoice_no}`} checked={selected.has(i.id)} onChange={() => toggle(i.id)} className="w-4 h-4 accent-emerald-600" />}</td>
                              <td className="py-3 px-2 font-medium text-slate-800">{i.invoice_no}</td>
                              <td className="py-3 px-2 text-slate-600">{i.period_key || fmtDate(i.invoice_date)}</td>
                              <td className="py-3 px-2 text-slate-600">{fmtDate(i.due_date)}</td>
                              <td className="py-3 px-2 text-right tabular-nums">{inr(i.total)}</td>
                              <td className="py-3 px-2 text-right tabular-nums font-semibold">{inr(i.balance)}</td>
                              <td className="py-3 px-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_STYLES[i.status] || STATUS_STYLES.issued}`}>{(i.status || "issued").replace("_", " ")}</span></td>
                              <td className="py-3 px-2"><button data-testid={`inv-pdf-${i.invoice_no}`} onClick={() => doDownload(i)} className="text-slate-500 hover:text-emerald-700 transition-colors"><Download className="w-4 h-4" /></button></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {selected.size > 0 && (
                  <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between p-4 rounded-md bg-emerald-50 border border-emerald-200">
                    <div>
                      <p className="text-xs font-semibold uppercase text-emerald-700">Pay selected</p>
                      <p className="font-heading text-2xl font-bold text-emerald-900">{inr(selectedAmount)}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(me.payment_qr?.image || me.payment_qr?.upi_id) && (
                        <Button data-testid="pay-qr-btn" onClick={() => setQrOpen(true)} className="bg-emerald-600 hover:bg-emerald-700"><QrCode className="w-4 h-4 mr-1.5" /> Scan QR to pay</Button>
                      )}
                      {me.portal?.razorpay_enabled ? (
                        <Button data-testid="pay-online-btn" onClick={payOnline} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700"><CreditCard className="w-4 h-4 mr-1.5" /> Pay online</Button>
                      ) : (!me.payment_qr?.image && !me.payment_qr?.upi_id && (
                        <span className="text-xs text-emerald-700 self-center">Online payments not configured — use manual reference</span>
                      ))}
                      <Button data-testid="pay-manual-btn" variant="outline" onClick={openManual}><FileText className="w-4 h-4 mr-1.5" /> I paid — enter reference</Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="history" className="mt-0">
                <p className="text-xs text-slate-500 mb-3">Every payment we've received against your account. Total lifetime paid <b className="text-slate-700 tabular-nums">{inr(totalPaid)}</b>.</p>
                {payments.length === 0 ? (
                  <div className="border-2 border-dashed border-slate-200 rounded-md p-10 text-center" data-testid="history-empty">
                    <Receipt className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500 text-sm">No payments yet. Once you settle an invoice, receipts will appear here for easy reference.</p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="history-list">
                    {payments.map((p) => {
                      const badge = p.method === "Razorpay" ? "bg-indigo-100 text-indigo-700" : p.source === "tenant_portal_manual_approved" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700";
                      return (
                        <div key={p.id} data-testid={`history-row-${p.receipt_no}`} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-md border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors">
                          <div className="w-10 h-10 rounded-md bg-emerald-50 flex items-center justify-center shrink-0"><Receipt className="w-5 h-5 text-emerald-600" /></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-baseline gap-x-2">
                              <p className="font-mono text-sm font-semibold text-slate-800">{p.receipt_no}</p>
                              <span className={`text-[10px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded ${badge}`}>{p.method}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">{fmtDate(p.payment_date)} · {(p.allocations || []).map((a) => a.invoice_no).filter(Boolean).join(", ") || "—"}</p>
                            {p.reference && <p className="text-[11px] text-slate-400 font-mono truncate">Ref: {p.reference}</p>}
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-heading text-lg font-bold tracking-tighter text-emerald-700 tabular-nums">{inr(p.amount)}</p>
                            {(p.allocations || []).length > 1 && <p className="text-[10px] text-slate-400">across {p.allocations.length} invoices</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </Card>

          <div className="space-y-6">
            <Card className="p-6 border-slate-200 shadow-sm">
              <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-4"><User className="w-5 h-5 text-emerald-600" /> Profile</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Tenant code</dt><dd className="font-medium text-slate-800">{me.tenant?.code}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Mobile</dt><dd className="font-medium text-slate-800">{me.tenant?.mobile || "-"}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Email</dt><dd className="font-medium text-slate-800 truncate max-w-[10rem]">{me.tenant?.email || "-"}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">GSTIN</dt><dd className="font-medium text-slate-800">{me.tenant?.gstin || "-"}</dd></div>
              </dl>
            </Card>
            {lease && (
              <Card className="p-6 border-slate-200 shadow-sm">
                <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-4"><Building2 className="w-5 h-5 text-emerald-600" /> Your Lease</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-3"><dt className="text-slate-500">Property</dt><dd className="font-medium text-slate-800 text-right">{prop?.name || "-"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Unit</dt><dd className="font-medium text-slate-800">{lease.unit || "-"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Agreement</dt><dd className="font-medium text-slate-800">{lease.agreement_no}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Term</dt><dd className="font-medium text-slate-800">{fmtDate(lease.start_date)} → {fmtDate(lease.end_date)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Rent due day</dt><dd className="font-medium text-slate-800">{lease.rent_due_day || "-"}</dd></div>
                </dl>
              </Card>
            )}
            {dep && (
              <Card className="p-6 border-slate-200 shadow-sm">
                <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-4"><ShieldCheck className="w-5 h-5 text-emerald-600" /> Deposit</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-500">Held</dt><dd className="font-medium text-slate-800 tabular-nums">{inr(dep.amount)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Received on</dt><dd className="font-medium text-slate-800">{fmtDate(dep.date_received)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Status</dt><dd><Badge className="capitalize bg-slate-100 text-slate-700">{dep.status || "held"}</Badge></dd></div>
                </dl>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Confirm your payment</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Once submitted, your landlord will verify and mark the invoice paid.</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Method">
              <select data-testid="manual-method" value={manual.method} onChange={(e) => setManual({ ...manual, method: e.target.value })} className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm bg-white">
                {["UPI", "NEFT", "IMPS", "RTGS", "Cash", "Cheque"].map((m) => <option key={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Amount (₹)"><Input data-testid="manual-amount" type="number" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })} /></Field>
          </div>
          <Field label="UPI / UTR / Reference No."><Input data-testid="manual-reference" value={manual.reference} onChange={(e) => setManual({ ...manual, reference: e.target.value })} placeholder="e.g. UPI-Ref 3XX8912345" /></Field>
          <Field label="Note (optional)"><Textarea data-testid="manual-note" value={manual.note} onChange={(e) => setManual({ ...manual, note: e.target.value })} placeholder="Any details you want your landlord to see" /></Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button data-testid="manual-submit" className="bg-emerald-600 hover:bg-emerald-700" onClick={submitManual} disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-md" data-testid="qr-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><QrCode className="w-5 h-5 text-emerald-600" /> Scan &amp; Pay {inr(selectedAmount)}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 -mt-1">Open PhonePe / GooglePay / Paytm / any UPI app, scan the code and pay <b>{inr(selectedAmount)}</b> to <b className="font-bold text-slate-900" data-testid="qr-owner-name">{me.owner?.name || me.payment_qr?.upi_name}</b>. Then tap "I've paid" to log the reference so your landlord can verify it.</p>
          {me.owner?.logo_image && <img src={me.owner.logo_image} alt={me.owner.name} data-testid="qr-owner-logo" className="h-10 object-contain self-center" />}
          {(me.payment_qr?.image || me.payment_qr?.upi_id) && (
            <div className="flex flex-col items-center gap-3">
              <div className="p-4 rounded-lg border-2 border-emerald-200 bg-white">
                {me.payment_qr.image
                  ? <img src={me.payment_qr.image} alt="Payment QR" className="w-56 h-56 object-contain" data-testid="qr-image" />
                  : <QRCodeCanvas data-testid="qr-image" value={`upi://pay?pa=${encodeURIComponent(me.payment_qr.upi_id)}&pn=${encodeURIComponent(me.payment_qr.upi_name || "")}&cu=INR${selectedAmount ? `&am=${selectedAmount}` : ""}`} size={224} />}
              </div>
              {me.payment_qr.upi_id && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500">UPI ID:</span>
                  <span className="font-mono font-medium text-slate-800" data-testid="qr-upi-id">{me.payment_qr.upi_id}</span>
                  <button data-testid="qr-copy-upi" onClick={() => { navigator.clipboard.writeText(me.payment_qr.upi_id); toast.success("UPI ID copied"); }} className="text-slate-400 hover:text-emerald-600 transition-colors"><Copy className="w-3.5 h-3.5" /></button>
                </div>
              )}
              {me.payment_qr.upi_name && <p className="text-xs text-slate-500">Paying to: <b className="text-slate-800 font-bold">{me.payment_qr.upi_name}</b></p>}
              {me.payment_qr.note && <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-center max-w-sm">{me.payment_qr.note}</p>}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setQrOpen(false)} data-testid="qr-close">Close</Button>
            <Button data-testid="qr-paid-btn" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setQrOpen(false); openManual(); }}><CheckCircle2 className="w-4 h-4 mr-1.5" /> I've paid — enter reference</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
